import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Trash2, X } from 'lucide-react';
import { useApi } from '../../../services/api/provider';
import { ManufacturerService } from '../../../services/api/domains/manufacturers/service';
import {
  useManufacturerCorporateStructure,
  useSetManufacturerParent,
  useRemoveManufacturerParent,
  useCreateManufacturerStubRelative,
} from '../../../services/api/domains/manufacturers/hooks';
import { ManufacturerSimilarMatch } from '../../../services/api/domains/manufacturers/types';
import { DEBOUNCE_DELAY } from '../../../shared/hooks/useAsyncSelect';
import LgmButton from '../../LgmButton';
import { GRAY_200, GRAY_300, GRAY_500, GRAY_900 } from 'theme/grayScale';
import { COLOR_DANGER } from 'theme/colors';

interface CorporateStructureTabProps {
  manufacturerId: number;
}

type Relationship = 'parent' | 'child';

interface MutationErrorLike {
  message: string;
  details?: unknown;
}

/** The server's `sendError()` shape is `{error, retryable, category}` (e.g.
 * the cycle-guard's 409) — surface that human-readable string when present,
 * since the axios-level message ("Request failed with status code 409")
 * isn't useful to a user. */
function mutationErrorMessage(error: MutationErrorLike | undefined): string {
  const details = error?.details as { error?: string } | undefined;
  return details?.error ?? error?.message ?? 'Something went wrong.';
}

// A 1-character query would flood the results with near-random matches (the
// server's own substring-match fallback in api/sql/manufacturers.js has the
// same 2-character gate for the same reason). Mirrors LinkToSourceModal.tsx's
// manufacturer-link-search minimum.
const MIN_SEARCH_LENGTH = 2;

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: GRAY_500,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  margin: 0,
};

const sectionHeaderRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '8px',
};

// Icon-only remove action (issue #654 Seq 16 QA follow-up) -- replaces the
// previous full-width "Remove Parent" text button, which visually dominated
// its row for a low-frequency action. Mirrors FacilitiesPanel.tsx's Trash2
// remove-row-icon convention. `title` gives a native hover tooltip in
// addition to `aria-label`'s accessible name, since a bare icon has no
// visible label of its own.
const iconButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: COLOR_DANGER,
  padding: '4px',
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
};

interface RelativePickerProps {
  manufacturerId: number;
  relationship: Relationship;
  onClose: () => void;
  /** Called after Set Parent / Create New succeeds, so the caller can
   * refetch the corporate-structure query — see issue #748. */
  onSaved: () => void;
}

/**
 * Search-or-create picker for Sub-task 14.4 — one merged input drives both
 * "search for an existing manufacturer" and "create a new one", mirroring
 * the debounced/abortable type-ahead pattern `ExternalLinkSearchPane.tsx`
 * and the manufacturer `LinkToSourceModal.tsx` already use for external-
 * source search (issue #654 Seq 16 QA follow-up — the original two-input,
 * click-to-search UI didn't match any other search surface in the app).
 */
const RelativePicker: React.FC<RelativePickerProps> = ({
  manufacturerId,
  relationship,
  onClose,
  onSaved,
}) => {
  const api = useApi();
  const manufacturerService = useMemo(
    () => new ManufacturerService(api),
    [api]
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [matches, setMatches] = useState<ManufacturerSimilarMatch[] | null>(
    null
  );
  // `similarity === 1` is only ever assigned by the backend for a genuine
  // exact (case-insensitive) name match — see scoreManufacturerNameSimilarity
  // in api/sql/sqlHelpers.js, which short-circuits to 1 only when the two
  // strings are literally equal. Covers both the single-exact-match branch
  // and the ambiguous-tie branch (findSimilarManufacturers in
  // api/sql/manufacturers.js), which returns multiple similarity:1 rows as
  // fuzzyMatches instead of a single exactMatch.
  const [hasExactMatch, setHasExactMatch] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  const setParentMutation = useSetManufacturerParent();
  const createStubMutation = useCreateManufacturerStubRelative();

  const isChild = relationship === 'child';
  const busy =
    setParentMutation.status === 'loading' ||
    createStubMutation.status === 'loading';

  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastRequestedTermRef = useRef('');
  // Aborts the prior in-flight request before a new one starts, so a
  // superseded search (an older keystroke's query) is genuinely cancelled
  // server-side rather than left to run to completion and hold a DB pool
  // slot for a result nobody will use — same pattern as
  // ExternalLinkSearchPane.tsx's PERF-004 fix.
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
      abortControllerRef.current?.abort();
    },
    []
  );

  const runSearch = useCallback(
    async (term: string) => {
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setIsSearching(true);
      setSearchError(null);

      try {
        const response = await manufacturerService.findSimilar(
          term,
          undefined,
          abortController.signal
        );
        if (term !== lastRequestedTermRef.current) return;
        const { exactMatch, fuzzyMatches } = response.data;
        setMatches(exactMatch ? [exactMatch, ...fuzzyMatches] : fuzzyMatches);
        setHasExactMatch(
          Boolean(exactMatch) || fuzzyMatches.some(m => m.similarity === 1)
        );
      } catch (err) {
        if (abortController.signal.aborted) return;
        if (term !== lastRequestedTermRef.current) return;
        setMatches(null);
        setHasExactMatch(false);
        setSearchError(err instanceof Error ? err.message : 'Search failed.');
      } finally {
        if (!abortController.signal.aborted) setIsSearching(false);
      }
    },
    [manufacturerService]
  );

  const handleSearchTermChange = (value: string) => {
    setSearchTerm(value);
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

    const trimmed = value.trim();
    lastRequestedTermRef.current = trimmed;
    if (trimmed.length < MIN_SEARCH_LENGTH) {
      // Clearing the input (or dropping below the minimum) must abort any
      // in-flight request too, or a fast type-then-clear leaves a
      // server-side request running with nothing left to ever supersede it.
      abortControllerRef.current?.abort();
      setIsSearching(false);
      setMatches(null);
      setHasExactMatch(false);
      setSearchError(null);
      return;
    }

    debounceTimeoutRef.current = setTimeout(() => {
      if (lastRequestedTermRef.current === trimmed) {
        runSearch(trimmed);
      }
    }, DEBOUNCE_DELAY);
  };

  const handleSearchNow = () => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    const trimmed = searchTerm.trim();
    if (trimmed.length < MIN_SEARCH_LENGTH) return;
    lastRequestedTermRef.current = trimmed;
    runSearch(trimmed);
  };

  const handleClear = () => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    abortControllerRef.current?.abort();
    lastRequestedTermRef.current = '';
    setSearchTerm('');
    setMatches(null);
    setHasExactMatch(false);
    setSearchError(null);
    setIsSearching(false);
  };

  const handleSelectExisting = (relatedId: number) => {
    // Setting THIS manufacturer's parent points parent_manufacturer_id at
    // relatedId; setting a CHILD instead points the OTHER manufacturer's
    // parent_manufacturer_id back at this one.
    const variables = isChild
      ? { manufacturerId: relatedId, parentManufacturerId: manufacturerId }
      : { manufacturerId, parentManufacturerId: relatedId };
    setParentMutation
      .mutate(variables)
      .then(() => {
        onSaved();
        onClose();
      })
      .catch(() => {
        // Surfaced via setParentMutation.error below; nothing further to do.
      });
  };

  const handleCreateNew = () => {
    const trimmed = searchTerm.trim();
    if (!trimmed || hasExactMatch) return;
    createStubMutation
      .mutate({ manufacturerId, manufacturer_name: trimmed, relationship })
      .then(() => {
        onSaved();
        onClose();
      })
      .catch(() => {
        // Surfaced via createStubMutation.error below; nothing further to do
        // -- also the server's own POST /:id/stub-relative 409 guard (an
        // exact-name-match race the debounce window could theoretically
        // miss) surfaces here the same way.
      });
  };

  const mutationError =
    setParentMutation.status === 'error'
      ? setParentMutation.error
      : createStubMutation.status === 'error'
        ? createStubMutation.error
        : undefined;

  const trimmedTerm = searchTerm.trim();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '8px',
        border: `1px solid ${GRAY_200}`,
        borderRadius: '4px',
      }}>
      {mutationError && (
        <div style={{ color: COLOR_DANGER, fontSize: '0.75rem' }}>
          {mutationErrorMessage(mutationError)}
        </div>
      )}
      <div style={{ position: 'relative', width: '100%' }}>
        <input
          type="text"
          aria-label={`Search or create a manufacturer to set as ${relationship}`}
          placeholder="Search existing manufacturer or type a new name"
          value={searchTerm}
          onChange={e => handleSearchTermChange(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSearchNow();
            }
          }}
          style={{
            width: '100%',
            padding: '6px 32px 6px 8px',
            fontSize: '0.8125rem',
            border: `1px solid ${GRAY_300}`,
            borderRadius: '4px',
            boxSizing: 'border-box',
          }}
        />
        {searchTerm && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={handleClear}
            style={{
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: GRAY_500,
              display: 'flex',
              padding: 0,
            }}>
            <X size={14} />
          </button>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px',
          flexWrap: 'wrap',
        }}>
        <LgmButton
          variant="outlined"
          disabled={busy || !trimmedTerm || hasExactMatch}
          title={
            hasExactMatch
              ? `A manufacturer named "${trimmedTerm}" already exists — select it above instead.`
              : undefined
          }
          onClick={handleCreateNew}>
          Create as New
        </LgmButton>
        <LgmButton
          variant="outlined"
          disabled={trimmedTerm.length < MIN_SEARCH_LENGTH}
          onClick={handleSearchNow}>
          Search
        </LgmButton>
        <LgmButton variant="flat" disabled={busy} onClick={onClose}>
          Cancel
        </LgmButton>
      </div>
      {isSearching && (
        <div style={{ fontSize: '0.75rem', color: GRAY_500 }}>Searching...</div>
      )}
      {searchError && (
        <div style={{ color: COLOR_DANGER, fontSize: '0.75rem' }}>
          {searchError}
        </div>
      )}
      {!isSearching && matches && matches.length === 0 && (
        <div style={{ fontSize: '0.75rem', color: GRAY_500 }}>
          No matches found.
        </div>
      )}
      {!isSearching && matches && matches.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {matches.map(match => (
            <LgmButton
              key={match.manufacturer_id}
              variant="outlined"
              disabled={busy}
              onClick={() => handleSelectExisting(match.manufacturer_id)}>
              {match.manufacturer_name} ({Math.round(match.similarity * 100)}
              %)
            </LgmButton>
          ))}
        </div>
      )}
      {hasExactMatch && (
        <div
          style={{ fontSize: '0.75rem', color: GRAY_500, fontStyle: 'italic' }}>
          A manufacturer named &quot;{trimmedTerm}&quot; already exists — select
          it above instead of creating a new one.
        </div>
      )}
    </div>
  );
};

/**
 * Manufacturer modal Corporate Structure tab (Sub-task 14.4, MFGV2-014) —
 * view and edit the parent/child hierarchy: set/remove this manufacturer's
 * parent, and add children (existing or stub) that point back at it. The
 * cycle-guard (`wouldCreateManufacturerParentCycle` in api/sql/manufacturers.js)
 * rejects invalid links server-side with a 409; `mutationErrorMessage` above
 * surfaces that message verbatim rather than a generic axios error string.
 */
const CorporateStructureTab: React.FC<CorporateStructureTabProps> = ({
  manufacturerId,
}) => {
  const structureQuery = useManufacturerCorporateStructure(manufacturerId);
  const removeParentMutation = useRemoveManufacturerParent();
  // Separate hook instance from removeParentMutation -- useMutation tracks
  // one shared status/error per instance (services/api/hooks.ts), so
  // removing a child through the SAME instance used for "remove this
  // manufacturer's own parent" would let one action's error bleed into the
  // other section's error banner.
  const removeChildMutation = useRemoveManufacturerParent();
  const [showParentPicker, setShowParentPicker] = useState(false);
  const [showChildPicker, setShowChildPicker] = useState(false);
  // Which child a remove is in flight (or most recently failed) for --
  // useMutation has no per-call identity, so this is what lets the error
  // banner and the disabled/pending row track a specific child.
  const [removingChildId, setRemovingChildId] = useState<number | null>(null);

  if (structureQuery.status === 'error') {
    return (
      <div style={{ color: COLOR_DANGER, fontSize: '13px', padding: '8px 0' }}>
        Failed to load corporate structure. Try closing and reopening the modal.
      </div>
    );
  }

  if (structureQuery.status === 'idle' || structureQuery.status === 'loading') {
    return (
      <div style={{ color: GRAY_500, fontSize: '13px', padding: '8px 0' }}>
        Loading corporate structure...
      </div>
    );
  }

  const { parent, children } = structureQuery.data;

  const handleRemoveParent = () => {
    removeParentMutation
      .mutate(manufacturerId)
      .then(() => structureQuery.refetch())
      .catch(() => {
        // Surfaced via removeParentMutation.error below.
      });
  };

  const handleRemoveChild = (child: {
    manufacturer_id: number;
    manufacturer_name: string;
  }) => {
    setRemovingChildId(child.manufacturer_id);
    removeChildMutation
      .mutate(child.manufacturer_id)
      .then(() => {
        setRemovingChildId(null);
        structureQuery.refetch();
      })
      .catch(() => {
        // Surfaced via removeChildMutation.error below, keyed to
        // removingChildId -- left set (not cleared) so the error banner can
        // still say which child failed.
      });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div>
        <div style={sectionHeaderRowStyle}>
          <h4 style={sectionTitleStyle}>Parent</h4>
          {!parent && !showParentPicker && (
            <LgmButton
              variant="outlined"
              onClick={() => setShowParentPicker(true)}>
              Set Parent
            </LgmButton>
          )}
        </div>
        {parent ? (
          <div style={rowStyle}>
            <span style={{ fontSize: '13px', color: GRAY_900 }}>
              {parent.manufacturer_name}
            </span>
            <button
              type="button"
              aria-label={`Remove parent ${parent.manufacturer_name}`}
              title="Remove parent"
              disabled={removeParentMutation.status === 'loading'}
              onClick={handleRemoveParent}
              style={iconButtonStyle}>
              <Trash2 size={14} />
            </button>
          </div>
        ) : showParentPicker ? (
          <RelativePicker
            manufacturerId={manufacturerId}
            relationship="parent"
            onClose={() => setShowParentPicker(false)}
            onSaved={() => structureQuery.refetch()}
          />
        ) : (
          <div style={{ fontSize: '13px', color: GRAY_500 }}>
            No parent set.
          </div>
        )}
        {removeParentMutation.status === 'error' && (
          <div
            style={{
              color: COLOR_DANGER,
              fontSize: '0.75rem',
              marginTop: '4px',
            }}>
            {mutationErrorMessage(removeParentMutation.error)}
          </div>
        )}
      </div>

      <div>
        <div style={sectionHeaderRowStyle}>
          <h4 style={sectionTitleStyle}>Children ({children.length})</h4>
          {!showChildPicker && (
            <LgmButton
              variant="outlined"
              onClick={() => setShowChildPicker(true)}>
              Add Child
            </LgmButton>
          )}
        </div>
        {children.length === 0 ? (
          <div
            style={{ fontSize: '13px', color: GRAY_500, marginBottom: '8px' }}>
            No subsidiaries recorded.
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              marginBottom: '8px',
            }}>
            {children.map(child => (
              <div
                key={child.manufacturer_id}
                style={{
                  ...rowStyle,
                  padding: '4px 0',
                  borderBottom: `1px solid ${GRAY_200}`,
                }}>
                <span style={{ fontSize: '13px', color: GRAY_900 }}>
                  {child.manufacturer_name}
                </span>
                <button
                  type="button"
                  aria-label={`Remove ${child.manufacturer_name} as subsidiary`}
                  title="Remove subsidiary"
                  disabled={removeChildMutation.status === 'loading'}
                  onClick={() => handleRemoveChild(child)}
                  style={iconButtonStyle}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
        {removeChildMutation.status === 'error' && removingChildId !== null && (
          <div
            style={{
              color: COLOR_DANGER,
              fontSize: '0.75rem',
              marginBottom: '8px',
            }}>
            {mutationErrorMessage(removeChildMutation.error)}
          </div>
        )}
        {showChildPicker && (
          <RelativePicker
            manufacturerId={manufacturerId}
            relationship="child"
            onClose={() => setShowChildPicker(false)}
            onSaved={() => structureQuery.refetch()}
          />
        )}
      </div>
    </div>
  );
};

export default CorporateStructureTab;
